import { useEffect, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import toast, { Toaster } from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import * as services from "../../services/saleng";

const logoRecommendation =
  "แนะนำไฟล์ PNG พื้นหลังโปร่งใส ขนาด 500 × 500 พิกเซล (อัตราส่วน 1:1)";

export default function EditBranch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shopId, setShopId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    branchCode: "",
    taxNumber: "",
    phoneNumber: "",
    email: "",
    address: "",
    image: "",
  });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    try {
      setShopId(jwtDecode(localStorage.getItem("accessToken"))?.id);
    } catch {}
  }, []);

  useEffect(() => {
    if (!shopId) return;

    services
      .getBranch(Number(id))
      .then((response) => {
        const branch = response.data.data;
        if (!branch) {
          toast.error("ไม่พบข้อมูลสาขา");
          return;
        }

        setFormData({
          name: branch.name || "",
          branchCode: branch.branchCode || "",
          taxNumber: branch.taxNumber || "",
          phoneNumber: branch.phoneNumber || "",
          email: branch.email || "",
          address: branch.address || "",
          image: branch.image || "",
        });
      })
      .catch(() => toast.error("โหลดข้อมูลสาขาไม่สำเร็จ"));
  }, [shopId, id]);

  const change = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const onDrop = (files) => {
    const file = files[0];
    if (!file) return;

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((current) => ({ ...current, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const save = async () => {
    try {
      let image = formData.image;
      if (imageFile) {
        const uploadData = new FormData();
        uploadData.append("file", imageFile);
        const response = await services.uploadFile(uploadData);
        if (!response.ok) throw new Error("อัปโหลดโลโก้ไม่สำเร็จ");

        const files = await response.json();
        image = files[0] || image;
      }

      await services.updateBranch(Number(id), { ...formData, image }, shopId);
      toast.success("บันทึกข้อมูลสาขาแล้ว");
      navigate("/branches");
    } catch (error) {
      toast.error(error.message || "บันทึกไม่สำเร็จ");
    }
  };

  return (
    <>
      <Toaster position="bottom-left" />
      <Breadcrumbs sx={{ mt: 2, mb: 2 }}>
        <Link href="/">หน้าหลัก</Link>
        <Link href="/branches">จัดการสาขา</Link>
        <Typography>แก้ไขสาขา</Typography>
      </Breadcrumbs>

      <Typography variant="h5" sx={{ mb: 2 }}>
        แก้ไขสาขา
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField name="name" label="ชื่อสาขา" value={formData.name} onChange={change} required />
          <TextField name="branchCode" label="รหัสสาขา" value={formData.branchCode} onChange={change} />
          <TextField name="taxNumber" label="เลขประจำตัวผู้เสียภาษี" value={formData.taxNumber} onChange={change} />
          <TextField name="phoneNumber" label="เบอร์โทรศัพท์" value={formData.phoneNumber} onChange={change} />
          <TextField name="email" label="อีเมล" value={formData.email} onChange={change} />
          <TextField name="address" label="ที่อยู่" value={formData.address} onChange={change} multiline minRows={3} sx={{ gridColumn: { md: "span 2" } }} />

          <Box sx={{ gridColumn: { md: "span 2" } }}>
            <Typography sx={{ mb: 0.5 }}>โลโก้ร้าน</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {logoRecommendation}
            </Typography>
            <Box
              {...getRootProps()}
              sx={{
                width: 200,
                height: 160,
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "grey.400",
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <input {...getInputProps()} />
              {formData.image ? (
                <img src={formData.image} alt="โลโก้ร้าน" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              ) : (
                <Box textAlign="center">
                  <CloudUploadIcon color="primary" sx={{ fontSize: 42 }} />
                  <Typography>ลากรูปมาวาง หรือคลิกเพื่อเลือกรูป</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <ButtonGroup sx={{ mt: 3 }} variant="contained" color="success">
          <Button onClick={save}>บันทึกและกลับ</Button>
          <Button onClick={() => navigate("/branches")}>ยกเลิก</Button>
        </ButtonGroup>
      </Paper>
    </>
  );
}
